import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchLeads, fetchRegisteredUsers } from "./adminLeadService";
import "../courses/adminCourses.css";

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
      })
    : "—";

const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
      })
    : "—";

const LEAD_SOURCE_LABELS = {
  ORGANIC:      "Organic",
  REFERRAL:     "Referral",
  WHATSAPP:     "WhatsApp",
  INSTAGRAM:    "Instagram",
  FACEBOOK:     "Facebook",
  SOCIAL_MEDIA: "Social Media",
  PAID_AD:      "Paid Ad",
  WALK_IN:      "Walk In",
  EMAIL:        "Email",
  WEBSITE:      "Website",
  OTHER:        "Other",
};

const LEAD_STATUS_BADGE = {
  NEW:             "ac-badge-gray",
  CONTACTED:       "ac-badge-blue",
  INTERESTED:      "ac-badge-purple",
  DEMO_SCHEDULED:  "ac-badge-orange",
  DEMO_DONE:       "ac-badge-purple",
  CONVERTED:       "ac-badge-green",
  NOT_INTERESTED:  "ac-badge-red",
  NURTURING:       "ac-badge-yellow",
  ENROLLED:        "ac-badge-green",
  LOST:            "ac-badge-red",
};

const toLabel = (str) =>
  str ? str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—";

const DEMO_STATUS_BADGE = {
  null:      "ac-badge-gray",
  PENDING:   "ac-badge-yellow",
  SCHEDULED: "ac-badge-blue",
  COMPLETED: "ac-badge-green",
  CANCELLED: "ac-badge-red",
};

/* ── Leads Tab ── */
const LeadsTab = () => {
  const navigate = useNavigate();
  const [leads, setLeads]     = useState([]);
  const [search, setSearch]   = useState("");
  const [loading, setLoading] = useState(true);
  const debounceRef           = useRef(null);

  const load = (q) => {
    setLoading(true);
    fetchLeads(q ? { search: q } : {})
      .then((r) => setLeads(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(""); }, []);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(val), 400);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <input
          className="ac-input"
          style={{ padding: "0.5rem 0.75rem", border: "1px solid #d1d5db", borderRadius: 6, fontSize: "0.88rem", width: 260 }}
          placeholder="Search leads…"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <button className="ac-btn ac-btn-primary" onClick={() => navigate("/admin/leads/new")}>
          + New Lead
        </button>
      </div>

      {loading && <div className="ac-loading">Loading…</div>}

      {!loading && leads.length === 0 && <div className="ac-empty">No leads found.</div>}

      {!loading && leads.length > 0 && (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Source</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id}>
                  <td><strong>{l.name}</strong></td>
                  <td style={{ fontSize: "0.85rem" }}>{l.email || "—"}</td>
                  <td style={{ fontSize: "0.85rem" }}>{l.whatsappNumber || "—"}</td>
                  <td><span className="ac-badge ac-badge-gray">{LEAD_SOURCE_LABELS[l.leadSource] || toLabel(l.leadSource)}</span></td>
                  <td><span className={`ac-badge ${LEAD_STATUS_BADGE[l.currentStatus] || "ac-badge-gray"}`}>{toLabel(l.currentStatus)}</span></td>
                  <td style={{ fontSize: "0.82rem" }}>{fmtDate(l.createdAt)}</td>
                  <td>
                    <div className="ac-actions">
                      <button className="ac-btn ac-btn-primary ac-btn-sm" onClick={() => navigate(`/admin/leads/${l.id}`)}>
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ── Registered Users Tab ── */
const RegisteredUsersTab = () => {
  const navigate    = useNavigate();
  const [users, setUsers]     = useState([]);
  const [search, setSearch]   = useState("");
  const [loading, setLoading] = useState(true);
  const debounceRef           = useRef(null);

  const load = (q) => {
    setLoading(true);
    fetchRegisteredUsers(q || "")
      .then((r) => setUsers(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(""); }, []);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(val), 400);
  };

  const handleCreateLead = (u) => {
    const params = new URLSearchParams({
      userId: u.userId,
      name:   u.name,
      email:  u.email || "",
      mobile: u.mobileNumber || "",
    });
    navigate(`/admin/leads/new?${params.toString()}`);
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <input
          style={{ padding: "0.5rem 0.75rem", border: "1px solid #d1d5db", borderRadius: 6, fontSize: "0.88rem", width: 260 }}
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      {loading && <div className="ac-loading">Loading…</div>}

      {!loading && users.length === 0 && <div className="ac-empty">No registered users found.</div>}

      {!loading && users.length > 0 && (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>WhatsApp</th>
                <th>Registered On</th>
                <th>Lead Source</th>
                <th>Free Demo Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const demoKey = u.freeDemoStatus || "null";
                return (
                  <tr key={u.userId}>
                    <td>
                      <strong>{u.name}</strong>
                      {u.suspended && (
                        <span className="ac-badge ac-badge-red" style={{ marginLeft: 6, fontSize: "0.68rem" }}>Suspended</span>
                      )}
                    </td>
                    <td style={{ fontSize: "0.85rem" }}>{u.email}</td>
                    <td style={{ fontSize: "0.85rem" }}>
                      {u.countryCode} {u.mobileNumber}
                      {u.whatsappEnabled && (
                        <span style={{ marginLeft: 4, color: "#16a34a", fontSize: "0.75rem" }}>✓ WA</span>
                      )}
                    </td>
                    <td style={{ fontSize: "0.82rem" }}>{fmtDateTime(u.registeredAt)}</td>
                    <td><span className="ac-badge ac-badge-gray">{toLabel(u.leadSource)}</span></td>
                    <td>
                      <span className={`ac-badge ${DEMO_STATUS_BADGE[demoKey] || "ac-badge-gray"}`}>
                        {u.freeDemoStatus ? toLabel(u.freeDemoStatus) : "No Demo"}
                      </span>
                      {u.freeDemoScheduledAt && (
                        <div style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: 2 }}>
                          {fmtDateTime(u.freeDemoScheduledAt)}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="ac-actions">
                        {u.linkedLeadId == null ? (
                          <button className="ac-btn ac-btn-primary ac-btn-sm" onClick={() => handleCreateLead(u)}>
                            Create Lead
                          </button>
                        ) : (
                          <button className="ac-btn ac-btn-ghost ac-btn-sm" onClick={() => navigate(`/admin/leads/${u.linkedLeadId}`)}>
                            View Lead
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ── Main Page ── */
const AdminLeadListPage = () => {
  const [tab, setTab] = useState("leads");

  return (
    <div>
      <div className="ac-header">
        <h2>Leads CRM</h2>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "2px solid #e5e7eb" }}>
        {[["leads", "📋 Leads"], ["registered", "👥 Registered Users"]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              padding: "8px 20px", border: "none", background: "none",
              fontWeight: 700, fontSize: "0.9rem", cursor: "pointer",
              borderBottom: tab === key ? "2px solid #2563eb" : "2px solid transparent",
              color: tab === key ? "#2563eb" : "#6b7280", marginBottom: -2,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "leads"      && <LeadsTab />}
      {tab === "registered" && <RegisteredUsersTab />}
    </div>
  );
};

export default AdminLeadListPage;
